import type { Nullable } from '@repo/common';
import type { Picture, PictureInput } from '@repo/frontend-shared';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSDialog } from '@repo/sds/SDSDialog';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import type { ImageData } from '@repo/ui/components/elements/ImageInput/ImageInput';
import { ImageInput } from '@repo/ui/components/elements/ImageInput/ImageInput';
import { createCommittedImageData } from '@repo/ui/components/elements/ImageInput/image-input-utils';
import { usePicture } from '@repo/ui/hooks/use-picture';

interface UploadPictureModalProps {
  isOpen: boolean;
  onClose: () => void;
  picture: Nullable<Partial<Picture>>;
  onSave: (payload: PictureInput) => void | Promise<void>;
  isPersisting: boolean;
}

export function UploadPictureModal({ picture, isOpen, onSave, onClose, isPersisting }: UploadPictureModalProps) {
  const { uploadPictureBatch, savePictureBatch } = usePicture();
  const committedValue: ImageData | null = picture?.thumbnailUrl ? createCommittedImageData(picture) : null;

  const handleChange = async (image: ImageData | null) => {
    if (!image) return;
    const batch = await uploadPictureBatch(image);
    await savePictureBatch(batch, onSave);
  };

  return (
    <SDSDialog isOpen={isOpen} onClose={onClose} title="Add photo">
      <SDSFlexbox direction="column" gap="button-large" fullWidth>
        <ImageInput
          value={committedValue}
          onChange={handleChange}
          displayName="Add a Profile Picture"
          aspectRatio="square"
          dataTestId={DataTestIds.MODALS.IMAGE_UPLOAD.UPLOAD_BTN}
          previewDataTestId={DataTestIds.MODALS.IMAGE_UPLOAD.AVATAR}
          previewAlt="Profile picture"
          allowRemove={false}
          isDisabled={isPersisting}
          editDataTestId={DataTestIds.MODALS.IMAGE_UPLOAD.EDIT_BTN}
        />
        <SDSFlexbox direction="row" justifyContent="flex-end" fullWidth>
          <SDSButton
            dataTestId={DataTestIds.MODALS.IMAGE_UPLOAD.CLOSE_BTN}
            type="button"
            text="Close"
            mode="outline"
            onClick={onClose}
            ariaLabel="Close upload modal"
          />
        </SDSFlexbox>
      </SDSFlexbox>
    </SDSDialog>
  );
}
